import { memo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'store';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// project imports
import NavItem from 'layout/MainLayout/Sidebar/MenuList/NavItem';
import { NavGroupProps } from 'layout/MainLayout/Sidebar/MenuList/NavGroup';
import config from 'config';

// assets
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { NavItemType } from 'types';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

interface NavCollapseProps {
  menu: NavGroupProps['item'];
  level: number;
}

const { borderRadius } = config;

const NavCollapse = ({ menu, level }: NavCollapseProps) => {
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const { openItem, drawerOpen } = useSelector((state) => state.menu);
  const [selected, setSelected] = useState<string | null | undefined>(null);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!selected ? menu.id : null);
  };

  const { pathname } = useLocation();

  useEffect(() => {
    const childrens = menu.children ? menu.children : [];
    childrens.forEach((item: NavItemType) => {
      if (item?.url && pathname.includes(item.url)) {
        setOpen(true);
      }
    });
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const Icon = menu.icon!;
  const menuIcon = menu.icon ? (
    <Icon
      strokeWidth={1.5}
      size="1.3rem"
      style={{ marginTop: 'auto', marginBottom: 'auto' }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selected === menu.id ? 8 : 6,
        height: selected === menu.id ? 8 : 6,
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  return (
    <>
      <ListItemButton
        sx={{
          borderRadius: `${borderRadius}px`,
          mb: 0.5,
          alignItems: 'flex-start',
          backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
          py: level > 1 ? 1 : 1.25,
          pl: drawerOpen ? `${level * 24}px` : '12px',
          '&.Mui-selected': {
            backgroundColor: theme.palette.brand.moss,
          },
        }}
        selected={menu.children?.some((child) => child.id === openItem[0])}
        onClick={handleClick}
      >
        <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 36 }}>
          {menuIcon}
        </ListItemIcon>
        {drawerOpen && (
          <ListItemText
            primary={
              <Typography
                variant={selected === menu.id ? 'h5' : 'body1'}
                color="inherit"
                sx={{ my: 'auto' }}
                title={String(menu.title)}
              >
                {menu.title}
              </Typography>
            }
            secondary={
              menu.caption && (
                <Typography
                  variant="caption"
                  sx={{ ...theme.typography.subMenuCaption }}
                  display="block"
                  gutterBottom
                >
                  {menu.caption}
                </Typography>
              )
            }
          />
        )}
        {drawerOpen &&
          (open ? (
            <IconChevronUp
              stroke={1.5}
              size="1rem"
              style={{ marginTop: 'auto', marginBottom: 'auto' }}
            />
          ) : (
            <IconChevronDown
              stroke={1.5}
              size="1rem"
              style={{ marginTop: 'auto', marginBottom: 'auto' }}
            />
          ))}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {open && (
          <List
            component="div"
            disablePadding
            sx={{
              position: 'relative',
              '&:after': {
                content: "''",
                position: 'absolute',
                left: drawerOpen ? '32px' : 0,
                top: 0,
                height: '100%',
                width: '1px',
                opacity: 0.3,
                background: theme.palette.primary.main,
              },
            }}
          >
            {menus}
          </List>
        )}
      </Collapse>
    </>
  );
};

export default memo(NavCollapse);
